import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {formatTimeRange} from "../../helpers/tooltips";

export class ShaperCircuitFunnel extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.graph = null;
        this.summaryId = `${this.id}-summary`;
        this.explainIssues = null;
    }

    title() {
        return "Network Funnel";
    }

    tooltip() {
        return "<h5>Network Funnel</h5><p>Shows utilization from the subscriber up through upstream sites.</p>";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.explainIssues !== null;
    }

    explain() {
        if (!this.explainIssues || this.explainIssues.length === 0) return null;
        return { region: "Network Funnel", issues: this.explainIssues };
    }

    buildContainer() {
        const base = super.buildContainer();
        const summary = document.createElement("div");
        summary.id = this.summaryId;
        summary.className = "mb-3";
        base.appendChild(summary);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new FunnelGraph(this.graphDivId());
    }

    channelRequests() {
        return ["CircuitFunnel"];
    }

    onMessage(msg) {
        if (msg.Data.target !== "circuit_funnel") {
            return;
        }
        const snapshot = msg.Data.blob;
        if (!snapshot) {
            return;
        }
        this.renderSummary(snapshot);
        this.graph?.update(snapshot);
        this.explainIssues = buildFunnelIssues(snapshot);
    }

    renderSummary(data) {
        const container = document.getElementById(this.summaryId);
        if (!container) {
            return;
        }
        container.innerHTML = "";
        const pathList = document.createElement("div");
        pathList.className = "d-flex flex-wrap gap-2 mb-2";
        const circuitBadge = document.createElement("span");
        circuitBadge.className = "badge bg-primary text-wrap";
        circuitBadge.textContent = "Subscriber";
        pathList.appendChild(circuitBadge);
        data.nodes.forEach((node) => {
            const badge = document.createElement("span");
            badge.className = "badge bg-secondary text-wrap";
            const down = node.download_capacity_mbps ? `${node.download_capacity_mbps.toFixed(0)} Mbps` : "N/A";
            badge.textContent = `${node.name} (${down})`;
            pathList.appendChild(badge);
        });
        container.appendChild(pathList);

        const latestIndex = data.ticks.length - 1;
        if (latestIndex < 0) {
            return;
        }
        const table = document.createElement("table");
        table.className = "table table-sm table-dark mb-0";
        const header = document.createElement("thead");
        const headerRow = document.createElement("tr");
        headerRow.innerHTML = "<th>Node</th><th class='text-end'>Utilization</th>";
        header.appendChild(headerRow);
        table.appendChild(header);
        const tbody = document.createElement("tbody");

        const circuitPercent = data.circuit_percent[latestIndex];
        const circuitRow = document.createElement("tr");
        circuitRow.innerHTML = `<td>Subscriber</td><td class="text-end">${formatPercent(circuitPercent)}</td>`;
        tbody.appendChild(circuitRow);

        data.nodes.forEach((node, idx) => {
            const series = data.site_series[idx];
            const percent = series ? series.percent_utilization[latestIndex] : null;
            const row = document.createElement("tr");
            row.innerHTML = `<td>${node.name}</td><td class="text-end">${formatPercent(percent)}</td>`;
            tbody.appendChild(row);
        });
        table.appendChild(tbody);
        container.appendChild(table);
    }
}

class FunnelGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(50)
            .withTimeAxis()
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option.yAxis = {
            type: "value",
            min: 0,
            max: 1,
            axisLabel: {
                formatter: (val) => `${(val * 100).toFixed(0)}%`,
            },
        };
        this.chart.setOption(this.option);
    }

    update(data) {
        if (!data || !data.ticks) {
            return;
        }
        this.chart.hideLoading();
        this.option.series = [];
        this.option.legend.data = [];
        this.option.xAxis.data = data.ticks;

        const addSeries = (name, values, color) => {
            this.option.series.push({
                name,
                type: "line",
                data: values.map((v) =>
                    v === null || v === undefined ? null : v / 100
                ),
                showSymbol: false,
                emphasis: { focus: "series" },
                lineStyle: { width: 2 },
                itemStyle: { color },
            });
            this.option.legend.data.push(name);
        };

        addSeries(
            "Subscriber",
            data.circuit_percent,
            window.graphPalette ? window.graphPalette[0] : "#4caf50"
        );

        data.nodes.forEach((node, idx) => {
            const series = data.site_series[idx];
            if (!series) return;
            addSeries(
                node.name,
                series.percent_utilization,
                window.graphPalette ? window.graphPalette[(idx + 1) % window.graphPalette.length] : undefined
            );
        });

        this.chart.setOption({
            legend: this.option.legend,
            xAxis: this.option.xAxis,
            yAxis: this.option.yAxis,
            series: this.option.series,
        });
    }
}

const formatPercent = (value) => {
    if (value === null || value === undefined) {
        return "—";
    }
    return `${value.toFixed(1)}%`;
};

function buildFunnelIssues(data) {
    const issues = [];
    if (!data || !Array.isArray(data.ticks) || !Array.isArray(data.nodes) || !Array.isArray(data.site_series)) {
        return issues;
    }
    const ticks = data.ticks;
    const threshold = 90;

    data.nodes.forEach((node, idx) => {
        const series = data.site_series[idx]?.percent_utilization;
        if (!Array.isArray(series)) return;
        const len = Math.min(series.length, ticks.length);
        if (len < 2) return;

        let total = 0;
        let above = 0;
        let firstIdx = -1;
        let lastIdx = -1;
        let maxVal = null;

        for (let i = 0; i < len; i += 1) {
            const v = series[i];
            if (!Number.isFinite(v)) continue;
            total += 1;
            if (maxVal === null || v > maxVal) maxVal = v;
            if (v >= threshold) {
                above += 1;
                if (firstIdx === -1) firstIdx = i;
                lastIdx = i;
            }
        }
        if (total === 0 || above === 0) return;

        const timeRange = (firstIdx >= 0 && lastIdx >= 0)
            ? formatTimeRange(ticks[firstIdx], ticks[lastIdx])
            : null;
        const pct = Math.round((above / total) * 100);
        const peak = maxVal !== null ? `${maxVal.toFixed(1)}%` : null;
        const nodeName = node && node.name ? String(node.name) : "Upstream node";
        const details = peak
            ? `Node: ${nodeName} - >=${threshold}% in ${pct}% of samples (peak ${peak})`
            : `Node: ${nodeName} - >=${threshold}% in ${pct}% of samples`;

        issues.push({
            time_range: timeRange,
            severity: "warn",
            issue: "Upstream utilization high",
            details,
        });
    });

    return issues;
}
